import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import snippets from '../../data/snippetsData';

const Snippets = () => {
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('');

  useEffect(() => {
    document.title = 'Snippets — Dmitri Malisev';
  }, []);

  const allTags = Array.from(new Set(snippets.flatMap(s => s.tags)));

  const filtered = snippets.filter(s => {
    const matchesSearch =
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.description.toLowerCase().includes(search.toLowerCase());
    const matchesTag = activeTag ? s.tags.includes(activeTag) : true;
    return matchesSearch && matchesTag;
  });

  return (
    <Container>
      <PageHeader>
        <SectionLabel>Snippets</SectionLabel>
        <PageTitle>Code snippets</PageTitle>
        <PageSubtitle>Common solutions, in multiple languages. Pick your tab, copy, and go.</PageSubtitle>
      </PageHeader>

      <Controls>
        <SearchInput
          type="text"
          placeholder="Search snippets..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <TagRow>
          <TagButton $active={!activeTag} onClick={() => setActiveTag('')}>All</TagButton>
          {allTags.map(tag => (
            <TagButton key={tag} $active={activeTag === tag} onClick={() => setActiveTag(tag)}>
              {tag}
            </TagButton>
          ))}
        </TagRow>
      </Controls>

      <SnippetGrid>
        {filtered.map(snippet => (
          <SnippetCard key={snippet.id} to={`/snippets/${snippet.id}`}>
            <CardTop>
              <CardTitle>{snippet.title}</CardTitle>
              <CardDesc>{snippet.description}</CardDesc>
            </CardTop>
            <CardFooter>
              <LangPills>
                {snippet.variants.map(v => (
                  <LangPill key={v.lang}>{v.label}</LangPill>
                ))}
              </LangPills>
              <TopicTags>
                {snippet.tags.map(tag => (
                  <TopicTag key={tag}>{tag}</TopicTag>
                ))}
              </TopicTags>
            </CardFooter>
          </SnippetCard>
        ))}
        {filtered.length === 0 && (
          <Empty>No snippets match your search.</Empty>
        )}
      </SnippetGrid>
    </Container>
  );
};

const Container = styled.div`
  padding: 4rem 0;
`;

const PageHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionLabel = styled.p`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--accent);
  margin: 0 0 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.375rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-primary);
  margin: 0;
`;

const Controls = styled.div`
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

const SearchInput = styled.input`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  font-family: var(--font-sans);
  color: var(--text-primary);
  width: 100%;
  max-width: 400px;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TagButton = styled.button`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  padding: 0.3rem 0.75rem;
  border-radius: 3px;
  border: 1px solid ${props => props.$active ? 'var(--accent)' : 'var(--border)'};
  background: ${props => props.$active ? 'var(--accent-subtle)' : 'transparent'};
  color: ${props => props.$active ? 'var(--accent)' : 'var(--text-muted)'};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const SnippetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SnippetCard = styled(Link)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.375rem 1.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-surface);
  text-decoration: none;
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--accent);
  }
`;

const CardTop = styled.div`
  margin-bottom: 1.25rem;
`;

const CardTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
`;

const CardDesc = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.6;
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LangPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const LangPill = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  border: 1px solid var(--border-accent);
  color: var(--accent);
  background: var(--accent-subtle);
`;

const TopicTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const TopicTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  border: 1px solid var(--border);
  color: var(--text-muted);
`;

const Empty = styled.p`
  color: var(--text-muted);
  font-size: 0.9375rem;
  grid-column: 1 / -1;
`;

export default Snippets;
